import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { startOfMonth, eachDayOfInterval, startOfDay, endOfMonth, isBefore } from 'date-fns';
import { useBooking } from '../hooks/useBooking';
import {
  calculateAvailableSlots,
  formatTimeForDisplay,
  getUnavailableDatesForMonth,
} from '../utils/availability';
import type { TimeSlot } from '../types';
import { BookingStepper } from '../components/BookingStepper';

import { Calendar } from '../components/ui/calendar';

export function DateTimeSelectionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const slotUnavailableError = (location.state as any)?.error ?? null;

  const {
    bookingState,
    setSelectedDate,
    setSelectedTime,
    businessListing,
  } = useBooking();


  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [unavailableDates, setUnavailableDates] = useState<Set<string>>(new Set());
  const [monthLoaded, setMonthLoaded] = useState(false);
  const [loadingMonth, setLoadingMonth] = useState(true);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const autoSelectedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { selectedService, selectedDate, selectedTime } = bookingState;

  // Redirect if no service selected
  useEffect(() => {
    if (!selectedService) {
      navigate('/book');
    }
  }, [selectedService, navigate]);

  // Fetch unavailable dates when month or service changes (debounced 300ms)
  useEffect(() => {
    if (!selectedService || !businessListing) return;

    let cancelled = false;
    setUnavailableDates(new Set());
    setLoadingMonth(true);
    setMonthLoaded(false);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      getUnavailableDatesForMonth({
        month: currentMonth,
        serviceDuration: selectedService.duration_minutes,
        listingId: businessListing.id,
        businessHours: businessListing.business_hours || {},
        minNoticeHours: businessListing.min_notice_hours || 0,
        bookingWindowDays: businessListing.booking_window_days || 60,
      }).then((dates) => {
        if (!cancelled) {
          setUnavailableDates(dates);
          setLoadingMonth(false);
          setMonthLoaded(true);
        }
      }).catch(() => {
        if (!cancelled) {
          const fallback = new Set<string>();
          for (const day of eachDayOfInterval({
            start: startOfMonth(currentMonth),
            end: endOfMonth(currentMonth),
          })) {
            if (!isBefore(startOfDay(day), startOfDay(new Date()))) {
              fallback.add(day.toDateString());
            }
          }
          setUnavailableDates(fallback);
          setLoadingMonth(false);
          setMonthLoaded(true);
        }
      });
    }, 300);

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [currentMonth, selectedService, businessListing]);

  // Auto-select the first available date once the month data has loaded
  useEffect(() => {
    if (!monthLoaded || autoSelectedRef.current) return;
    if (selectedDate) return; // user already picked one

    const today = startOfDay(new Date());
    const monthEnd = endOfMonth(currentMonth);
    const rangeStart = today > startOfMonth(currentMonth) ? today : startOfMonth(currentMonth);
    const days = eachDayOfInterval({ start: rangeStart, end: monthEnd });

    for (const day of days) {
      if (isBefore(startOfDay(day), today)) continue;
      if (!unavailableDates.has(day.toDateString())) {
        autoSelectedRef.current = true;
        setSelectedDate(day);
        setSelectedTime(null);
        return;
      }
    }
  }, [monthLoaded, unavailableDates, currentMonth, selectedDate, setSelectedDate, setSelectedTime]);

  // Load available slots when date changes
  useEffect(() => {
    if (!selectedDate || !selectedService || !businessListing) return;

    let cancelled = false;
    const loadAvailability = async () => {
      setLoading(true);
      try {
        const slots = await calculateAvailableSlots({
          date: selectedDate,
          serviceDuration: selectedService.duration_minutes,
          listingId: businessListing.id,
          businessHours: businessListing.business_hours || {},
          bufferMinutes: 0,
          minNoticeHours: businessListing.min_notice_hours || 0,
        });

        if (!cancelled) setAvailableSlots(slots);
      } catch (error) {
        console.error('Error loading availability:', error);
        if (!cancelled) setAvailableSlots([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAvailability();
    return () => { cancelled = true; };
  }, [selectedDate, selectedService, businessListing]);

  // Early return AFTER all hooks
  if (!selectedService) {
    return null;
  }

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setSelectedTime(null);
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };

  const handleContinue = () => {
    if (selectedDate && selectedTime) {
      navigate('/book/details');
    }
  };

  const availableSlotsToShow = availableSlots.filter(slot => slot.available);
  const showContinue = selectedDate && selectedTime;

  return (
    <div className="min-h-screen bg-site-bg text-site-text pb-24">
      {/* The selected day in the site's primary color */}
      <style>{`
        .cal-day-selected { background-color: var(--site-primary) !important; color: var(--site-on-primary) !important; }
        .cal-day-selected:hover { background-color: var(--site-primary) !important; filter: brightness(0.9); }
      `}</style>

      {/* Header with Stepper */}
      <BookingStepper currentStep={2} />

      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Slot unavailable error (redirected from confirmation page) */}
        {slotUnavailableError && (
          <div className="bg-site-warning-soft border border-site-warning/40 rounded-2xl p-4 mb-4 text-site-warning text-sm">
            {slotUnavailableError}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[430px_minmax(0,1fr)] gap-4 items-start">
          {/* Calendar */}
          <div className="bg-site-bg rounded-2xl border border-site-border p-4">
            <h2 className="text-lg font-semibold text-site-text mb-2">
              Select a Date
            </h2>

            <div className="w-full">
              <Calendar
                mode="single"
                selected={selectedDate ?? undefined}
                onSelect={handleDateSelect}
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                // ADD THIS PROP:
                fixedWeeks 
                disabled={[
                  { before: new Date() },
                  (_date: Date) => !monthLoaded || loadingMonth,
                  (date: Date) => unavailableDates.has(date.toDateString()),
                ]}
                modifiers={{
                  unavailable: (date: Date) => unavailableDates.has(date.toDateString()),
                }}
                modifiersClassNames={{
                  unavailable: 'line-through',
                }}
                className="w-full rounded-lg p-1"
              />
            </div>

            {loadingMonth && (
              <p className="text-sm text-site-muted text-center mt-2">
                Checking availability…
              </p>
            )}
          </div>

          {/* Time Slots */}
          <div className="bg-site-bg rounded-2xl border border-site-border p-4 min-h-[460px]">
            <h2 className="text-lg font-semibold text-site-text mb-3">
              Select a Time
            </h2>

            {!selectedDate ? (
              <div className="h-[360px] flex items-center justify-center text-center">
                <p className="text-site-muted">
                  Pick a date to see available times.
                </p>
              </div>
            ) : loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-site-primary" />
              </div>
            ) : availableSlotsToShow.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 xl:grid-cols-5 gap-2">
                {availableSlotsToShow.map((slot) => {
                  const isSelected = selectedTime === slot.time;

                  return (
                    <button
                      key={slot.time}
                      onClick={() => handleTimeSelect(slot.time)}
                      className={`py-2.5 px-2 rounded-full border-2 transition-all text-sm font-medium ${
                        isSelected
                          ? 'bg-site-primary text-site-on-primary border-site-primary'
                          : 'border-site-border text-site-text'
                      }`}
                    >
                      {formatTimeForDisplay(slot.time)}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-site-muted">
                  No available time slots for this date.
                </p>
                <p className="text-sm text-site-muted mt-2">
                  Please select a different date.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky Continue Button */}
      {showContinue && (
        <div className="fixed bottom-0 inset-x-0 z-30 px-4 py-3">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={handleContinue}
              className="w-full bg-site-primary text-site-on-primary border border-site-primary-outline font-semibold py-3.5 rounded-full transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
